import { useEffect, useRef, useState } from 'react';
import { App as CapacitorApp, AppState } from '@capacitor/app';
import { supabase } from '../lib/supabaseClient';

export interface AppLifecycleState {
  isActive: boolean;
  isBackground: boolean;
  lastActiveTime: Date | null;
  lastBackgroundTime: Date | null;
}

export interface AppLifecycleHook {
  state: AppLifecycleState;
  refreshSession: () => Promise<void>;
  isRefreshing: boolean;
}

export const useAppLifecycle = (): AppLifecycleHook => {
  const [state, setState] = useState<AppLifecycleState>({
    isActive: true,
    isBackground: false,
    lastActiveTime: new Date(),
    lastBackgroundTime: null
  });
  
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const backgroundStartRef = useRef<Date | null>(null);
  const sessionRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshRef = useRef<Date | null>(null);
  const foregroundDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const backgroundDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Manual session refresh function
  const refreshSession = async (skipRateLimit: boolean = false): Promise<void> => {
    if (isRefreshing) {
      console.log('[AppLifecycle] Session refresh already in progress, skipping');
      return;
    }
    
    // Rate limiting: Only for periodic timer, NOT for foreground refresh
    if (!skipRateLimit) {
      const now = new Date();
      if (lastRefreshRef.current && (now.getTime() - lastRefreshRef.current.getTime()) < 30000) {
        console.log('[AppLifecycle] Session refresh rate limited, skipping');
        return;
      }
    }
    
    setIsRefreshing(true);
    lastRefreshRef.current = new Date();
    
    try {
      console.log('[AppLifecycle] Refreshing session...');
      
      // Force refresh the session
      const { data: { session }, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('[AppLifecycle] Session refresh failed:', error);
        throw error;
      }
      
      if (session) {
        console.log('[AppLifecycle] Session refreshed successfully');
      } else {
        console.warn('[AppLifecycle] No session returned from refresh');
      }
    } catch (error) {
      console.error('[AppLifecycle] Session refresh error:', error);
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  };

  // Set up periodic session refresh when app is active
  const startSessionRefreshTimer = () => {
    if (sessionRefreshTimerRef.current) {
      clearInterval(sessionRefreshTimerRef.current);
    }
    
    // Refresh session every 45 minutes (Supabase tokens expire in 1 hour)
    sessionRefreshTimerRef.current = setInterval(async () => {
      try {
        await refreshSession();
      } catch (error) {
        console.error('[AppLifecycle] Periodic session refresh failed:', error);
      }
    }, 45 * 60 * 1000); // 45 minutes
    
    console.log('[AppLifecycle] Session refresh timer started');
  };

  // Stop session refresh timer
  const stopSessionRefreshTimer = () => {
    if (sessionRefreshTimerRef.current) {
      clearInterval(sessionRefreshTimerRef.current);
      sessionRefreshTimerRef.current = null;
      console.log('[AppLifecycle] Session refresh timer stopped');
    }
  };

  // Debounced background handler
  const handleAppBackground = () => {
    if (backgroundDebounceRef.current) {
      clearTimeout(backgroundDebounceRef.current);
    }
    
    backgroundDebounceRef.current = setTimeout(() => {
      const now = new Date();
      backgroundStartRef.current = now;
      
      setState(prev => {
        if (prev.isBackground) return prev; // Already background
        
        return {
          ...prev,
          isActive: false,
          isBackground: true,
          lastBackgroundTime: now
        };
      });
      
      // Don't stop session refresh completely - just reduce frequency
      stopSessionRefreshTimer();
      
      console.log('[AppLifecycle] App went to background');
    }, 100); // 100ms debounce
  };

  // Debounced foreground handler
  const handleAppForeground = () => {
    if (foregroundDebounceRef.current) {
      clearTimeout(foregroundDebounceRef.current);
    }
    
    foregroundDebounceRef.current = setTimeout(async () => {
      const now = new Date();
      const backgroundDuration = backgroundStartRef.current 
        ? now.getTime() - backgroundStartRef.current.getTime()
        : 0;
      
      setState(prev => {
        if (prev.isActive) return prev; // Already active
        
        return {
          ...prev,
          isActive: true,
          isBackground: false,
          lastActiveTime: now
        };
      });
      
      console.log(`[AppLifecycle] App came to foreground after ${Math.round(backgroundDuration / 1000)}s`);
      
      // ALWAYS refresh session on foreground (skip rate limiting)
      // This ensures session is valid whenever user returns to app
      try {
        console.log('[AppLifecycle] App came to foreground - refreshing session');
        await refreshSession(true); // Skip rate limit for foreground refresh
        console.log('[AppLifecycle] Session refreshed successfully on foreground');
      } catch (error) {
        console.error('[AppLifecycle] Session refresh failed on foreground:', error);
        
        // Fallback: Try to restore session from storage
        try {
          console.log('[AppLifecycle] Attempting session restoration from storage...');
          const { data: { session }, error: getSessionError } = await supabase.auth.getSession();
          
          if (getSessionError || !session) {
            console.error('[AppLifecycle] Session restoration failed - user may need to re-login');
          } else {
            console.log('[AppLifecycle] ✅ Session restored from storage');
          }
        } catch (restoreError) {
          console.error('[AppLifecycle] Session restoration error:', restoreError);
        }
      }
      
      // Q3 FIX: Sync IAP receipts to prevent subscription date conflicts
      // When app comes to foreground, check Apple/Google receipt and update subscription_end_date
      try {
        // Dynamically import to avoid circular dependencies
        const { iapService } = await import('../services/iapService');
        
        // Restore purchases will sync receipt expiry dates to Supabase
        console.log('[AppLifecycle] Syncing IAP receipts on foreground...');
        await iapService.restorePurchases();
        console.log('[AppLifecycle] ✅ IAP receipts synced successfully');
      } catch (iapError) {
        // Non-critical error - app can continue without IAP sync
        console.warn('[AppLifecycle] IAP sync failed (non-critical):', iapError);
      }
      
      // Restart session refresh timer
      startSessionRefreshTimer();
      backgroundStartRef.current = null;
    }, 100); // 100ms debounce
  };

  // Set up app lifecycle listeners
  useEffect(() => {
    let stateChangeListener: any;
    let resumeListener: any;
    let pauseListener: any;
    let isCapacitorListenersActive = false;
    
    const setupListeners = async () => {
      try {
        // Listen for app state changes
        stateChangeListener = await CapacitorApp.addListener('appStateChange', (state: AppState) => {
          console.log('[AppLifecycle] App state changed:', state);
          
          if (state.isActive) {
            handleAppForeground();
          } else {
            handleAppBackground();
          }
        });
        
        // Listen for app resume events
        resumeListener = await CapacitorApp.addListener('resume', () => {
          console.log('[AppLifecycle] App resumed');
          handleAppForeground();
        });
        
        // Listen for app pause events  
        pauseListener = await CapacitorApp.addListener('pause', () => {
          console.log('[AppLifecycle] App paused');
          handleAppBackground();
        });
        
        isCapacitorListenersActive = true;
        console.log('[AppLifecycle] Capacitor listeners set up successfully');
        
        // Start initial session refresh timer
        startSessionRefreshTimer();
        
      } catch (error) {
        console.error('[AppLifecycle] Failed to set up Capacitor listeners:', error);
        
        // Fallback: use document visibility change for web
        const handleVisibilityChange = () => {
          if (document.hidden) {
            handleAppBackground();
          } else {
            handleAppForeground();
          }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        console.log('[AppLifecycle] Web visibility listeners set up as fallback');
        
        // Start session refresh timer even on web
        startSessionRefreshTimer();
        
        return () => {
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          stopSessionRefreshTimer();
        };
      }
    };
    
    setupListeners();
    
    return () => {
      // Clean up listeners
      stateChangeListener?.remove();
      resumeListener?.remove();
      pauseListener?.remove();
      
      // Clear debounce timers
      if (foregroundDebounceRef.current) {
        clearTimeout(foregroundDebounceRef.current);
      }
      if (backgroundDebounceRef.current) {
        clearTimeout(backgroundDebounceRef.current);
      }
      
      stopSessionRefreshTimer();
    };
  }, []);

  return {
    state,
    refreshSession,
    isRefreshing
  };
}; 